import { Alert, Box, Typography } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';
import FilterSidebar from 'components/filter-sidebar';
import FlexBox from 'components/flex-box';
import Paper from 'components/paper';

import { useAccountState } from 'contexts/account/account-context';
import { AddressAccess } from 'contexts/account/types/account-company.type';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router';
import OrderFeedTabsHeader from 'views/order-feed/components/order-feed-tabs-header';
import { OrderFeedSettingsProvider, useOrderFeedSettingsState } from 'views/order-feed/context/order-feed-settings-context';
import { OrderFeedProvider } from 'views/order-feed/context/order-feed-context';
import { MySupplyPlanProvider } from 'views/order-feed/features/my-supply-plan/context/my-supply-plan-context';
import { SiteAutocomplete } from 'views/order-feed/components/site-autocomplete';
import { useSelectedSite } from 'views/order-feed/features/my-supply-plan/hooks/use-selected-site';
import OrderList from 'views/order-feed/features/my-supply-plan/features/ofd-1.0/components/order-list/order-list';
import { MySupplyPlanParams } from 'views/order-feed/features/my-supply-plan/types/my-supply-plan-params.type';
import { AscCertificationStatus } from 'views/site-settings/types/asc-certification-status.type';
import ASCSmallCheck from 'components/asc/asc-small-check';

type Props = {};

const ListOrderSupplyPlan: React.FC<Props> = () => {
  const params = useParams<MySupplyPlanParams>();
  const history = useHistory();
  const intl = useIntl();
  const { account } = useAccountState();
  const {
    orderSettingsQuery: { data: orderSettings },
    siteSettingsQuery: { data: siteSettings },
  } = useOrderFeedSettingsState();
  const isAscCompliant = siteSettings?.ascStatus === AscCertificationStatus.AscCertified;
  const { selectedSite } = useSelectedSite();

  // #region STATE

  const [selectedSiteId, setSelectedSiteId] = useState<string>('');

  // #endregion STATE

  // #region HELPERS

  const isSiteActiveInErp = (site: AddressAccess) => !!site.isActive && !!site.siteErpId && !!site.address_1;

  // #endregion HELPERS

  // #region HOOKS

  useEffect(() => {
    setSelectedSiteId(
      params.siteErpId && params.globalSiteId ? `${String(params.siteErpId)}/${String(params.globalSiteId)}` : ''
    );
  }, [params.siteErpId, params.globalSiteId]);

  useEffect(() => {
    if (account.company?.addressAccess.length === 1 && isSiteActiveInErp(account.company?.addressAccess[0])) {
      history.push(
        `/order-feed/supply-plan/${String(account.company?.addressAccess[0].siteErpId)}/${String(
          account.company?.addressAccess[0].id
        )}`
      );
    }
  }, [account.company?.addressAccess, history]);

  // #endregion HOOKS

  return (
    <>
      <OrderFeedTabsHeader
        utils={
          <Breadcrumbs
            crumbs={
              selectedSiteId
                ? [
                    {
                      text:
                        account.company?.addressAccess.find((s) => s.siteErpId === params.siteErpId)?.name || selectedSiteId,
                      url: `${params.siteErpId}/${params.globalSiteId}`,
                    },
                  ]
                : undefined
            }
          />
        }
      />
      {!!orderSettings?.isUnderMaintenance && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          <FormattedMessage id={`orderFeed.alert.maintenance`} />
          {orderSettings?.endsAt && (
            <Typography sx={{ display: 'flex', alignItems: 'center' }}>
              {intl.formatMessage({ id: 'orderFeed.alert.endsAt' })}
              {dayjs(orderSettings.endsAt).format('L LT')}
            </Typography>
          )}
        </Alert>
      )}
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <FilterSidebar sx={{ justifyContent: 'space-between' }}>
          <Box minWidth={150} display={'flex'} alignSelf={'center'} marginBottom={8}>
            {selectedSiteId && isAscCompliant && <ASCSmallCheck />}
          </Box>
          <SiteAutocomplete
            value={selectedSite || null}
            route={{
              basePath: '/order-feed/supply-plan',
              clearPath: '/order-feed/supply-plan/sites',
              getPath: (site) =>
                account.company?.features.hasOrderFreeDelivery2 && site.isOfd2Enabled
                  ? `/order-feed/supply-plan/order-free-delivery/${site.siteErpId}/${site.globalSiteId}/orders`
                  : `/order-feed/supply-plan/${site.siteErpId}/${site.globalSiteId}`,
            }}
          />
        </FilterSidebar>

        <Paper transparent>
          <FlexBox>
            <OrderList />
          </FlexBox>
        </Paper>
      </Box>
    </>
  );
};

const ListOrderSupplyPlanContainer: React.FC<Props> = () => (
  <OrderFeedProvider>
    <OrderFeedSettingsProvider>
      <MySupplyPlanProvider>
        <ListOrderSupplyPlan />
      </MySupplyPlanProvider>
    </OrderFeedSettingsProvider>
  </OrderFeedProvider>
);

export default ListOrderSupplyPlanContainer;
